import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { 
  Search, 
  Filter, 
  Download, 
  Calendar,
  Building,
  Euro,
  FileText,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Award
} from 'lucide-react';
import api from '../services/api';
import TenderModal from '../components/TenderModal';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Tender {
  id: number;
  cig: string;
  oggetto: string;
  tipo_appalto: string;
  importo_base: number;
  importo_aggiudicazione: number;
  stato: string;
  data_pubblicazione: string;
  data_scadenza: string;
  aggiudicatario: string;
  comune_nome: string;
  provincia: string;
  rup: string;
  link_dettagli: string;
}

const Tenders: React.FC = () => {
  const [page, setPage] = useState(1);
  const [perPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    stato: '',
    tipo: '',
    comune: '',
    minAmount: '',
    maxAmount: '',
    dateFrom: '',
    dateTo: '',
  });
  const [selectedTender, setSelectedTender] = useState<Tender | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [sortBy, setSortBy] = useState<'date' | 'amount' | 'cig'>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Fetch tenders
  const { data: tendersData, isLoading } = useQuery(
    ['tenders', page, perPage, filters, searchTerm, sortBy, sortOrder],
    () => api.getTenders({
      page,
      limit: perPage,
      search: searchTerm,
      ...filters,
      sortBy,
      sortOrder,
    }),
    { keepPreviousData: true }
  );

  // Fetch filter options
  const { data: filterOptions } = useQuery('filterOptions', api.getFilterOptions);

  // Filter and sort tenders
  const filteredTenders = useMemo(() => {
    if (!tendersData?.data) return [];
    
    let filtered = [...tendersData.data];
    
    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.cig.toLowerCase().includes(search) ||
        t.oggetto.toLowerCase().includes(search) ||
        t.aggiudicatario?.toLowerCase().includes(search) ||
        t.comune_nome?.toLowerCase().includes(search)
      );
    }
    
    // Apply filters
    if (filters.stato) {
      filtered = filtered.filter(t => t.stato === filters.stato);
    }
    if (filters.tipo) {
      filtered = filtered.filter(t => t.tipo_appalto === filters.tipo);
    }
    if (filters.comune) {
      filtered = filtered.filter(t => t.comune_nome === filters.comune);
    }
    if (filters.minAmount) {
      filtered = filtered.filter(t => t.importo_aggiudicazione >= parseFloat(filters.minAmount));
    }
    if (filters.maxAmount) {
      filtered = filtered.filter(t => t.importo_aggiudicazione <= parseFloat(filters.maxAmount));
    }
    
    return filtered;
  }, [tendersData, searchTerm, filters]);

  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      const data = await api.exportTenders(format, filters);
      // Handle download
      const blob = new Blob([data], { 
        type: format === 'csv' ? 'text/csv' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bandi_${new Date().toISOString().split('T')[0]}.${format}`;
      a.click();
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const getStatoBadgeClass = (stato: string) => {
    switch (stato?.toLowerCase()) {
      case 'aggiudicata':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'in corso':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'annullata':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const totalPages = Math.ceil((tendersData?.total || 0) / perPage);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
          Bandi e Gare
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Gestione completa di tutti i bandi pubblici
        </p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-4 mb-6">
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Cerca per CIG, oggetto, azienda..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
              />
            </div>
          </div>

          {/* Filter Buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('csv')}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export CSV
            </button>
            <button
              onClick={() => handleExport('excel')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Download className="h-4 w-4" />
              Export Excel
            </button>
          </div>
        </div>

        {/* Advanced Filters */}
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <select
              value={filters.stato}
              onChange={(e) => setFilters({ ...filters, stato: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Tutti gli stati</option>
              {Array.isArray(filterOptions?.stati) ? filterOptions.stati.map(stato => (
                <option key={stato} value={stato}>{stato}</option>
              )) : null}
            </select>

            <select
              value={filters.tipo}
              onChange={(e) => setFilters({ ...filters, tipo: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Tutti i tipi</option>
              {Array.isArray(filterOptions?.tipi) ? filterOptions.tipi.map(tipo => (
                <option key={tipo} value={tipo}>{tipo}</option>
              )) : null}
            </select>

            <select
              value={filters.comune}
              onChange={(e) => setFilters({ ...filters, comune: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            >
              <option value="">Tutti i comuni</option>
              {Array.isArray(filterOptions?.comuni) ? filterOptions.comuni.map(comune => (
                <option key={comune.id} value={comune.nome}>
                  {comune.nome} ({comune.provincia})
                </option>
              )) : null}
            </select>

            <input
              type="number"
              placeholder="Importo min"
              value={filters.minAmount}
              onChange={(e) => setFilters({ ...filters, minAmount: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />

            <input
              type="number"
              placeholder="Importo max"
              value={filters.maxAmount}
              onChange={(e) => setFilters({ ...filters, maxAmount: e.target.value })}
              className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
            />

            <button
              onClick={() => {
                setFilters({
                  stato: '',
                  tipo: '',
                  comune: '',
                  minAmount: '',
                  maxAmount: '',
                  dateFrom: '',
                  dateTo: '',
                });
                setSearchTerm('');
              }}
              className="px-3 py-2 bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 text-sm"
            >
              Reset Filtri
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
        Trovati <span className="font-semibold">{tendersData?.total || 0}</span> bandi
      </div>

      {/* Tenders Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  CIG
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Oggetto
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Comune
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Tipo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                    onClick={() => {
                      setSortBy('amount');
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    }}>
                  Importo ↕
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Stato
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer"
                    onClick={() => {
                      setSortBy('date');
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    }}>
                  Data Pub. ↕
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Aggiudicatario
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  </td>
                </tr>
              ) : filteredTenders.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                    Nessun bando trovato
                  </td>
                </tr>
              ) : (
                Array.isArray(filteredTenders) ? filteredTenders.map((tender) => (
                  <tr key={tender.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600 dark:text-blue-400">
                      {tender.cig}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                      <div className="max-w-xs truncate" title={tender.oggetto}>
                        {tender.oggetto}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      <div className="flex items-center">
                        <Building className="h-4 w-4 mr-1 text-gray-400" />
                        {tender.comune_nome}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {tender.tipo_appalto}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(tender.importo_aggiudicazione)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatoBadgeClass(tender.stato)}`}>
                        {tender.stato}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(tender.data_pubblicazione)}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                      <div className="max-w-xs truncate" title={tender.aggiudicatario}>
                        {tender.aggiudicatario || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => {
                          setSelectedTender(tender);
                          setShowModal(true);
                        }}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300 mr-3"
                      >
                        <FileText className="h-4 w-4" />
                      </button>
                      {tender.link_dettagli && (
                        <a
                          href={tender.link_dettagli}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-300"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                    </td>
                  </tr>
                )) : null
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 flex items-center justify-between border-t border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-700 dark:text-gray-300">
            Pagina {page} di {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const pageNum = i + 1;
              return (
                <button
                  key={pageNum}
                  onClick={() => setPage(pageNum)}
                  className={`px-3 py-1 border rounded-md ${
                    page === pageNum
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal */}
      {showModal && selectedTender && (
        <TenderModal
          tender={selectedTender}
          onClose={() => {
            setShowModal(false);
            setSelectedTender(null);
          }}
        />
      )}
    </div>
  );
};

export default Tenders;